export type HasPermissionFn = (permission: string) => boolean

/**
 * Bidirectional rename groups — any grant in a group satisfies any nav gate in the same group.
 * SW/DD atomized pages are exact-match only (RBAC-SLICE-3/5) — no umbrella / stale sales.workflow.view bridge.
 */
export const NAV_PERMISSION_ALIAS_GROUPS: readonly (readonly string[])[] = [
  ['sales.dashboard.view', 'sales.dashboard_old.view', 'sales.performance.view'],
  ['sales.open_pipeline.view', 'sales.pipeline.view'],
  ['sales.sales_detail.view', 'sales.sale_detail.view'],
  ['surveys.surveys.view', 'site.surveys.view', 'surveys.view'],
  // One group — shared 'inquiries.view' must not overwrite surveys↔sales aliases.
  ['surveys.inquiries.view', 'sales.inquiries.view', 'inquiries.view'],
  ['machines.machines.view', 'machines.view'],
  ['saleshistory.saleshistory.view', 'saleshistory.view'],
  ['pim.products.view', 'pim.view'],
  ['pim.categories.view', 'pim.view'],
] as const

/** Non-SW/DD umbrellas only (pim.view). */
const NAV_UMBRELLA_ROLE_COVERAGE: Readonly<Record<string, readonly string[]>> = {
  'pim.view': ['pim.products.view', 'pim.categories.view'],
}

function buildBidirectionalAliasLookup(): Map<string, ReadonlySet<string>> {
  const lookup = new Map<string, Set<string>>()

  for (const group of NAV_PERMISSION_ALIAS_GROUPS) {
    const equivalent = new Set(group)
    for (const permission of group) {
      lookup.set(permission, equivalent)
    }
  }

  return lookup
}

const NAV_BIDIRECTIONAL_ALIASES = buildBidirectionalAliasLookup()

/** All permission strings that participate in a bidirectional navigation alias group. */
export function isAliasedNavigationPermission(permission: string): boolean {
  return (
    NAV_BIDIRECTIONAL_ALIASES.has(permission) ||
    Object.values(NAV_UMBRELLA_ROLE_COVERAGE).some((covered) => covered.includes(permission))
  )
}

/** Expand a nav gate to permission strings that should satisfy route/meta checks. */
export function navigationPermissionAliases(permission: string): ReadonlySet<string> {
  const options = new Set<string>([permission])

  const bidirectional = NAV_BIDIRECTIONAL_ALIASES.get(permission)
  if (bidirectional) {
    for (const alias of bidirectional) {
      options.add(alias)
    }
  }

  for (const [umbrella, coveredGates] of Object.entries(NAV_UMBRELLA_ROLE_COVERAGE)) {
    if (coveredGates.includes(permission)) {
      options.add(umbrella)
    }
  }

  return options
}

/**
 * Navigation tree gate used by NavigationTreeRenderer and useNavigationConfig.
 * Exact match + non-SW/DD renames. No SW/DD umbrella expansion (RBAC-SLICE-5).
 */
export function hasNavigationItemPermission(
  hasPermission: HasPermissionFn,
  itemPermission: string | null | undefined,
): boolean {
  if (!itemPermission) {
    return true
  }

  if (hasPermission(itemPermission)) {
    return true
  }

  const bidirectional = NAV_BIDIRECTIONAL_ALIASES.get(itemPermission)
  if (bidirectional && [...bidirectional].some((permission) => hasPermission(permission))) {
    return true
  }

  for (const [umbrella, coveredGates] of Object.entries(NAV_UMBRELLA_ROLE_COVERAGE)) {
    if (coveredGates.includes(itemPermission) && hasPermission(umbrella)) {
      return true
    }
  }

  return false
}

/** Route/meta helper: canonical permission plus known legacy aliases for the same nav link. */
export function navigationViewPermissionOptions(canonical: string): string[] {
  return [...navigationPermissionAliases(canonical)]
}

/** Route guard helper: user needs at least one grant from any expanded permission in the list. */
export function hasAnyNavigationItemPermission(
  hasPermission: HasPermissionFn,
  itemPermissions: string[],
): boolean {
  return itemPermissions.some((permission) => hasNavigationItemPermission(hasPermission, permission))
}
